import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getInvestorApiUrl } from "../../utils/config";
import { ApiRequestParams, handleFetchApi } from "../../utils/api";

// Define types for the state

export interface KycDetails {
  [key: string]: any; // You can specify the actual structure here
}

interface AccountResponse {
  [key: string]: any; // Specify actual account structure if known
}

interface LedgerState {
  ledgerResponse: any | null; // The response from the POST Ledger API
  accountResponse: AccountResponse[]; // Array to store account data
  loading: boolean; // Loading state
  error: string; // Error state
  kycDetails: KycDetails; // KYC details
  postKycDetails: any | string; // Response after posting KYC details
}

// Initial state
const initialState: LedgerState = {
  ledgerResponse: null,
  accountResponse: [],
  loading: false,
  error: "",
  kycDetails: {},
  postKycDetails: "",
};

// Async thunk to post ledger configuration
export const postLedgerConfiguration = createAsyncThunk<
  any, // The type for the return data
  any, // The type for the argument (payload)
  { rejectValue: string } // The type for the rejected value (error)
>(
  "ledger/postLedgerConfiguration",
  async (payload, thunkApi) => {
    try {
           const params: ApiRequestParams = {
              baseUrl: getInvestorApiUrl(),
              endpoint: `Ledger`,
              payload,
              method:"POST"
            }
            const response = await handleFetchApi(params);
      // const response = await axios.post(`${baseUrl}/Ledger`, payload);
      return response.data; // Return the API response
    } catch (error :any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to post ledger configuration"
      );
    }
  }
);

// Async thunk to get KYC details
export const getKycDetails = createAsyncThunk<
  KycDetails, // The type for the returned data
  string, // The argument (investorId)
  { rejectValue: string } // Type for the rejected value
>(
  "ledger/getKycDetails",
  async (investorId, thunkApi) => {
    try {
      const params: ApiRequestParams = {
        baseUrl: getInvestorApiUrl(),
        endpoint: `kyc?investorid=${investorId}`,
      };
      const response = await handleFetchApi(params);
      // Ensure that only the serializable `data` is returned
      return response.data; // This will exclude any non-serializable values like headers
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to get KYC details"
      );
    }
  }
);


// Async thunk to post KYC details
export const postKycDetails = createAsyncThunk<
  any, // The type for the returned data
  any, // The argument (investorId)
  { rejectValue: string } // Type for the rejected value
>(
  "ledger/postKycDetails",
  async (investorId, thunkApi) => {
    try {
      const params: ApiRequestParams = {
        baseUrl: getInvestorApiUrl(),
        endpoint: `kyc?investorid=${investorId}`,
        method:"POST"
      }
      const response = await handleFetchApi(params);
      // const response = await axios.post(`${baseUrl}/kyc?investorid=${investorId}`);
      return response.data; // Return the response after posting KYC details
    } catch (error :any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to post KYC details"
      );
    }
  }
);

// Async thunk to get account data
export const fetchAccountData = createAsyncThunk<
  AccountResponse[], // The type for the returned data (account response)
  string, // The argument (investorId)
  { rejectValue: string } // Type for the rejected value
>(
  "ledger/fetchAccountData",
  async (investorId, thunkApi) => {
    try {
      const params: ApiRequestParams = {
        baseUrl: getInvestorApiUrl(),
        endpoint: `Account?investorId=${investorId}`,
      }
      const response = await handleFetchApi(params);
      // const baseUrl = getInvestorApiUrl();
      // const response = await axios.get(`${baseUrl}/Account?investorId=${investorId}`);
      return response.data.body; // Return the account data
    } catch (error :any) {
      return thunkApi.rejectWithValue(
        error.response?.data || "Failed to fetch account data"
      );
    }
  }
);

// Create slice
const ledgerSlice = createSlice({
  name: "ledger",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Post Ledger Configuration
      .addCase(postLedgerConfiguration.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(postLedgerConfiguration.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.ledgerResponse = action.payload;
      })
      .addCase(postLedgerConfiguration.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.ledgerResponse = null;
        state.error = action.payload;
      })

      // Get KYC Details
      .addCase(getKycDetails.pending, (state) => {
        state.loading = true;
        state.kycDetails = "";
      })
      .addCase(getKycDetails.fulfilled, (state, action: PayloadAction<KycDetails>) => {
        state.loading = false;
        state.kycDetails = action.payload;
      })
      .addCase(getKycDetails.rejected, (state, action:  PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch KYC details";
      })

      // Post KYC Details
      .addCase(postKycDetails.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(postKycDetails.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.postKycDetails = action.payload;
      })
      .addCase(postKycDetails.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Failed to post KYC details";
      })

      // Fetch Account Data
      .addCase(fetchAccountData.pending, (state) => {
        state.loading = true;
        state.error = "";
        // state.accountResponse = [];
      })
      .addCase(fetchAccountData.fulfilled, (state, action: PayloadAction<AccountResponse[]>) => {
        state.loading = false;
        state.accountResponse = action.payload;
      })
      .addCase(fetchAccountData.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.accountResponse = [];
        state.error = action.payload || "Failed to fetch account data";
      });
  },
});

export default ledgerSlice.reducer;
